import * as THREE from 'three';
import { lightShare } from '../../sim/light';
import { hash3 } from '../fishSwim';
import { WATER_FOG, attachSkin, noFog, type CausticUniforms, type ExhibitPaint, type FogUniforms, type PaintUniforms, type ReefPaint } from '../skin';
import { SKIN_TILE_PX } from '../skinTiles';
import { CAUSTIC_CONTRAST, CAUSTIC_DIRECTIONS } from '../aquariumView';
import { FADE_PER_M, FADE_TO_M, PAINT_HEX, SILT_M, VISIBLE } from '../exhibitView';
import type { HourSun } from '../sunlight';

/**
 * A habitat scene's floor and its scenery (M110): a height field drawn as a grid in the whole tank's pixel-art paint
 * (`skin.ts`: basalt where it is steep, sand to silt with depth, the reef's pavement where `pavementAt` picks it),
 * lit by the light that reaches each point's depth and reddened least where deepest, as the whole tank's floor is
 * (`ExhibitView.setLight`), with caustics where the light is strong; basalt boulders on it; and the water's fog for
 * the plain materials standing on it.
 */

/** A floor, drawn: its height (m, the surface 0, so -depth) and its pavement's share (0 sand to 1 reef) at a point. */
export interface FloorShape {
  heightAt(x: number, z: number): number;
  pavementAt(x: number, z: number): number;
}

/**
 * A floor's grid (M169): its points' x and z, each ascending, the grid every x by every z, so it may be finer where the
 * camera looks and coarser out in the water (`refinedGrid`), or even (`squareGrid`).
 */
export interface FloorGrid {
  xs: Float64Array;
  zs: Float64Array;
}

/** A grid `sizeM` square about the scene's middle, its points `stepM` apart. */
export function squareGrid(sizeM: number, stepM: number): FloorGrid {
  const n = Math.round(sizeM / stepM) + 1;
  const axis = Float64Array.from({ length: n }, (_, k) => -sizeM / 2 + k * stepM);
  return { xs: axis, zs: axis.slice() };
}

/**
 * A grid `sizeM` square about the scene's middle, its points `fineM` apart within the box `x` by `z` and `stepM` apart
 * outside it, its edges on the box's (M169: Maui Nui Reef's shelves and crevices drawn where the camera reaches).
 */
export function refinedGrid(sizeM: number, stepM: number, fineM: number, x: readonly [number, number], z: readonly [number, number]): FloorGrid {
  const axis = ([a, b]: readonly [number, number]): Float64Array => {
    const half = sizeM / 2;
    const out: number[] = [];
    const run = (from: number, to: number, step: number): void => {
      const n = Math.max(1, Math.round((to - from) / step));
      for (let k = 0; k < n; k++) out.push(from + ((to - from) * k) / n);
    };
    run(-half, a, stepM);
    run(a, b, fineM);
    run(b, half, stepM);
    out.push(half);
    return Float64Array.from(out);
  };
  return { xs: axis(x), zs: axis(z) };
}

/** The cell of an ascending axis a value falls in, and how far across it, 0 to 1 (held to the axis's ends). */
function cellOf(axis: Float64Array, v: number): [number, number] {
  const n = axis.length;
  if (v <= (axis[0] as number)) return [0, 0];
  if (v >= (axis[n - 1] as number)) return [n - 2, 1 - 1e-9];
  let [lo, hi] = [0, n - 1];
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if ((axis[mid] as number) <= v) lo = mid;
    else hi = mid;
  }
  return [lo, (v - (axis[lo] as number)) / ((axis[lo + 1] as number) - (axis[lo] as number))];
}

/**
 * A floor as it is drawn (M118): its heights at a grid's points (`FloorGrid`; M169, finer where the camera looks), and
 * between them the triangles `PaintedFloor` draws them as, so what stands on it or swims over it is on the floor seen,
 * and each height a look-up (an outcrop's seat samples the floor under its every base vertex: on Maui Nui Reef 1.6
 * million heights). Its pavement the shape's own. Past the grid, its edge's. `highest` stands over every height it
 * gives: its grid's highest, and a micrometer more for one between its points rounded up (by parts in 10^16).
 */
export function drawnFloor(shape: FloorShape, grid: FloorGrid): FloorShape & { highest: number } {
  const { xs, zs } = grid;
  const n = xs.length;
  const heights = new Float64Array(n * zs.length);
  for (let r = 0; r < zs.length; r++) for (let c = 0; c < n; c++) heights[r * n + c] = shape.heightAt(xs[c] as number, zs[r] as number);
  return {
    heightAt(x, z) {
      const [c, fx] = cellOf(xs, x);
      const [r, fz] = cellOf(zs, z);
      const a = r * n + c;
      const [h00, h10, h01, h11] = [heights[a] as number, heights[a + 1] as number, heights[a + n] as number, heights[a + n + 1] as number];
      // The grid's two triangles a square, (a, a + n, a + 1) and (a + 1, a + n, a + n + 1), as `PaintedFloor` lays them.
      return fx + fz <= 1 ? h00 + fx * (h10 - h00) + fz * (h01 - h00) : h11 + (1 - fx) * (h01 - h11) + (1 - fz) * (h10 - h11);
    },
    pavementAt: (x, z) => shape.pavementAt(x, z),
    highest: heights.reduce((m, h) => Math.max(m, h), -Infinity) + 1e-6,
  };
}

/** Smooth value noise on a lattice `cellM` apart, 0 to 1, by a place's seed and a salt: the drawing's own, never the simulation's. */
export function noise(seed: number, salt: number, x: number, z: number, cellM: number): number {
  const [u, v] = [x / cellM, z / cellM];
  const [i, k] = [Math.floor(u), Math.floor(v)];
  const [fx, fz] = [u - i, v - k];
  const [sx, sz] = [fx * fx * (3 - 2 * fx), fz * fz * (3 - 2 * fz)];
  const h = (a: number, b: number): number => hash3(seed + salt * 7919, a, b);
  return (h(i, k) * (1 - sx) + h(i + 1, k) * sx) * (1 - sz) + (h(i, k + 1) * (1 - sx) + h(i + 1, k + 1) * sx) * sz;
}

/**
 * Cellular noise on a lattice `cellM` apart (M177): each cell's one point, jittered within it by a place's seed and a
 * salt, and for a point the distances to the nearest of them and the next, m, and the nearest's own draw, 0 to 1 (so a
 * region is one block, its edge where the two distances meet). The drawing's own, never the simulation's.
 */
export function cells(seed: number, salt: number, x: number, z: number, cellM: number): [number, number, number] {
  const [u, v] = [x / cellM, z / cellM];
  const [i, k] = [Math.floor(u), Math.floor(v)];
  let [f1, f2, id] = [Infinity, Infinity, 0];
  for (let di = -1; di <= 1; di++) {
    for (let dk = -1; dk <= 1; dk++) {
      const [a, b] = [i + di, k + dk];
      const s = seed + salt * 7919;
      const [px, pz] = [a + 0.1 + 0.8 * hash3(s, a * 2, b), b + 0.1 + 0.8 * hash3(s, a * 2 + 1, b)];
      const d = Math.hypot(px - u, pz - v) * cellM;
      if (d < f1) [f2, f1, id] = [f1, d, hash3(s + 1, a, b)];
      else if (d < f2) f2 = d;
    }
  }
  return [f1, f2, id];
}

/** The light a share of full is seen by, the eye's log scale floored at `VISIBLE` (the whole tank's, `ExhibitView.setLight`). */
export function seenLight(share: number): number {
  return VISIBLE + (1 - VISIBLE) * Math.min(1, Math.max(0, (Math.log10(Math.max(share, 1e-9)) + 4) / 4));
}

/**
 * How far the light's strength moves, as a share, before the painted rock is lit again (M177's review; JUDGED): a
 * point's light moves under a thousandth of its range at it, its caustics a hundredth of their strength.
 */
const EXTRAS_RELIGHT = 0.01;

/** The mean of a palette's colors. */
const meanOf = (hexes: readonly number[], weights?: readonly number[]): THREE.Color =>
  weights ? hexes.reduce((m, hex, k) => m.add(new THREE.Color(hex).multiplyScalar(weights[k] as number)), new THREE.Color(0, 0, 0)) : hexes.map((hex) => new THREE.Color(hex)).reduce((m, c) => m.add(c), new THREE.Color(0, 0, 0)).multiplyScalar(1 / hexes.length);

/** Maui Nui Reef's materials as hex (M177, `ReefPaint`): its colors sRGB before the water's light, the rest as given. */
export type ReefPaintHex = Omit<ReefPaint, 'limestone' | 'basalt' | 'sand' | 'turf' | 'coralline' | 'rubble'> & Record<'limestone' | 'basalt' | 'sand' | 'turf' | 'coralline' | 'rubble', readonly number[]>;

export class PaintedFloor {
  readonly mesh: THREE.Mesh;
  readonly skin: Partial<FogUniforms & PaintUniforms>;
  private readonly colors: THREE.BufferAttribute;
  private readonly water: THREE.BufferAttribute;
  private readonly caustic: THREE.BufferAttribute;
  /** Each point's hard bottom's turf, coralline, rubble and basalt, and its recess (M177, Maui Nui Reef's paint), where the paint is the reef's. */
  private readonly sub: THREE.BufferAttribute | null;
  private readonly recess: THREE.BufferAttribute | null;
  /** Each point's pavement's own coral, the share of it and the share of that bleached, where the paint has coral. */
  private readonly coral: THREE.BufferAttribute | null;
  private readonly depth: Float32Array;
  /** Each point's own shade, 0 to 1, its light multiplied by (M169: Maui Nui Reef's recesses darker), 1 unless set (`setShade`). */
  private readonly shade: Float32Array;
  /**
   * Other meshes painted as its hard bottom (M177, `paintMesh`: Maui Nui Reef's outcrops and loose rocks), each lit as
   * its points are, by the light at its own depth.
   */
  private readonly extras: { depth: Float32Array; logShare: Float32Array; share: Float32Array; shade: Float32Array; fade: Float32Array; water: THREE.BufferAttribute; caustic: THREE.BufferAttribute }[] = [];
  /** Each point's light share's logarithm at the clarity last lit by, and its reds', greens' and blues' fade with depth. */
  private logShare: Float32Array;
  /** Each point's light share itself, ten to its logarithm, so a relight multiplies and raises nothing (M169's review). */
  private share: Float32Array;
  /** Each point's reds', greens' and blues' fade with depth, and that times its shade's (`setShade`, cooled by `shadeCool`). */
  private readonly depthFade: Float32Array;
  private readonly fade: Float32Array;
  private litKd = Number.NaN;
  /** The light's strength the points were last lit by: lit again only when it or the clarity changes (review M122). */
  private litStrength = Number.NaN;
  /** The light's strength the painted rock was last lit by, and the light last given (`paintMesh` lights by it). */
  private extrasStrength = Number.NaN;
  private lastSun: HourSun | null = null;
  private readonly causticDrift: CausticUniforms['uCausticDrift'] | null;
  readonly pavement: Float32Array;

  /**
   * @param grid the floor's points (`FloorGrid`)
   * @param texelM the paint's texel, m
   * @param rockDeg the slopes rock begins and is all rock at, degrees
   * @param caustics whether the shallows' caustics are laid on it
   * @param look the paint's patches' size and warp, and the pavement's own coral (`ExhibitPaint`, M125: Maui Nui
   *   Reef's), set by `setCoral`; its own materials in place of the whole tank's palette (`ReefPaintHex`, M177: Maui
   *   Nui Reef's, the v5 brief's; its sand's contrast, M117, and the v4 brief's palette, M169, retired with it); its
   *   edges broken up and its caustics varied (`ExhibitPaint.edges`,
   *   `causticVary`; M174); and its shade's cool (`shadeCool`, M174: each of its red, green and blue taken to its own
   *   power of a point's shade, so a recess goes cooler as it goes darker; all 1 unless said, a gray darkening); and its
   *   caustics' web's own texel (`causticTexelM`, M180; the floor's unless said)
   */
  constructor(shape: FloorShape, grid: FloorGrid, texelM: number, rockDeg: readonly [number, number], caustics: boolean, look: Pick<ExhibitPaint, 'coral' | 'patchTexels' | 'patchWarp' | 'edges' | 'causticVary'> & { shadeCool?: readonly [number, number, number]; reef?: ReefPaintHex; causticTexelM?: number } = {}) {
    const { xs, zs } = grid;
    const n = xs.length;
    const count = n * zs.length;
    const positions = new Float32Array(count * 3);
    this.depth = new Float32Array(count);
    this.pavement = new Float32Array(count);
    for (let r = 0; r < zs.length; r++) {
      for (let c = 0; c < n; c++) {
        const [x, z] = [xs[c] as number, zs[r] as number];
        const y = shape.heightAt(x, z);
        const i = r * n + c;
        positions.set([x, y, z], i * 3);
        this.depth[i] = -y;
        this.pavement[i] = shape.pavementAt(x, z);
      }
    }
    const index = new Uint32Array((n - 1) * (zs.length - 1) * 6);
    let k = 0;
    for (let r = 0; r < zs.length - 1; r++) {
      for (let c = 0; c < n - 1; c++) {
        const a = r * n + c;
        index.set([a, a + n, a + 1, a + 1, a + n, a + n + 1], k);
        k += 6;
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setIndex(new THREE.BufferAttribute(index, 1));
    geometry.computeVertexNormals();
    const normals = geometry.getAttribute('normal') as THREE.BufferAttribute;
    const slope = new Float32Array(count);
    this.slope = slope;
    this.rockDeg = rockDeg;
    for (let i = 0; i < count; i++) slope[i] = (Math.acos(Math.min(1, Math.abs(normals.getY(i)))) * 180) / Math.PI;
    this.colors = new THREE.BufferAttribute(new Float32Array(count * 3).fill(1), 3);
    this.water = new THREE.BufferAttribute(new Float32Array(count * 3).fill(1), 3);
    this.caustic = new THREE.BufferAttribute(new Float32Array(count), 1);
    this.logShare = new Float32Array(count);
    this.share = new Float32Array(count);
    this.shade = new Float32Array(count).fill(1);
    this.depthFade = new Float32Array(count * 3);
    this.fade = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const z = Math.min(this.depth[i] as number, FADE_TO_M);
      for (let k = 0; k < 3; k++) this.depthFade[i * 3 + k] = Math.exp(-(FADE_PER_M[k] as number) * z);
    }
    geometry.setAttribute('color', this.colors);
    geometry.setAttribute('aSkinLand', new THREE.BufferAttribute(new Float32Array(count), 1));
    geometry.setAttribute('aSkinReef', new THREE.BufferAttribute(this.pavement, 1));
    geometry.setAttribute('aSlope', new THREE.BufferAttribute(slope, 1));
    geometry.setAttribute('aDepth', new THREE.BufferAttribute(this.depth, 1));
    geometry.setAttribute('aWater', this.water);
    geometry.setAttribute('aCaustic', this.caustic);
    this.coral = look.coral ? new THREE.BufferAttribute(new Float32Array(count * 2), 2) : null;
    if (this.coral) geometry.setAttribute('aCoral', this.coral);
    this.sub = look.reef ? new THREE.BufferAttribute(new Float32Array(count * 4), 4) : null;
    this.recess = look.reef ? new THREE.BufferAttribute(new Float32Array(count), 1) : null;
    if (this.sub && this.recess) {
      geometry.setAttribute('aSub', this.sub);
      geometry.setAttribute('aRecess', this.recess);
    }
    // Both its sides drawn where it paints the kit's rock as well (`paintMesh`; M177's review: the kit's parts are open at
    // places, their rock's own material two-sided).
    const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, metalness: 0, side: look.reef ? THREE.DoubleSide : THREE.FrontSide });
    const linear = (hex: number): THREE.Color => new THREE.Color(hex);
    const { shadeCool = [1, 1, 1], reef: reefHex, causticTexelM = texelM, ...rest } = look;
    this.shadeCool = shadeCool;
    this.shadeFade();
    this.meanSand = meanOf(PAINT_HEX.sand);
    this.meanBasalt = meanOf(PAINT_HEX.basalt);
    const paint: ExhibitPaint = {
      sand: PAINT_HEX.sand.map(linear), silt: PAINT_HEX.silt.map(linear), basalt: PAINT_HEX.basalt.map(linear), coralline: PAINT_HEX.coralline.map(linear),
      rockFromDeg: rockDeg[0], rockToDeg: rockDeg[1], siltFromM: SILT_M[0], siltToM: SILT_M[1], ...rest,
      ...(reefHex ? { reef: { ...reefHex, limestone: reefHex.limestone.map(linear), basalt: reefHex.basalt.map(linear), sand: reefHex.sand.map(linear), turf: reefHex.turf.map(linear), coralline: reefHex.coralline.map(linear), rubble: reefHex.rubble.map(linear) } } : {}),
    };
    // The reef's sand and its stone, the skirt's beside it, their own means (M177): the sand's five, the limestone's
    // three lightest (its main tones, `ReefPaint`), its coralline none (drawn by the surveys' share, a few percent).
    if (reefHex) {
      this.meanSand = meanOf(reefHex.sand);
      this.meanBasalt = meanOf(reefHex.limestone.slice(0, 3));
    }
    const skin = attachSkin(material, { texel: texelM, causticTexel: causticTexelM, caustics, fog: true, paint });
    if (skin.uCausticContrast) skin.uCausticContrast.value = CAUSTIC_CONTRAST;
    if (skin.uCausticSurface) skin.uCausticSurface.value = 0;
    this.causticDrift = skin.uCausticDrift ?? null;
    this.skin = skin;
    this.causticTexelM = causticTexelM;
    this.mesh = new THREE.Mesh(geometry, material);
  }

  /** The caustics' web's texel, m, its tile what their drift wraps at. */
  private readonly causticTexelM: number;
  private readonly shadeCool: readonly [number, number, number];
  private readonly slope: Float32Array;
  /** Its palette's means, the sand's and its rock's (`paintAt`). */
  private meanSand: THREE.Color;
  private meanBasalt: THREE.Color;
  private readonly rockDeg: readonly [number, number];

  /**
   * A point's paint, the mean of the colors its texels are drawn in before the water's light (M125, for the skirt beside
   * the floor's edge; the review's: in the mean sand, the skirt had stood 2.4 times as bright as the pavement and rock
   * there): its pavement's share its tint; the rest rock as steep as it is (`rockDeg`) or sand. The pavement's own coral,
   * its coralline and the silt left out (the coral and the coralline a few percent of the pavement since M177's paint,
   * whose skirt alone reads this; the silt from `SILT_M`'s 40 m).
   */
  paintAt(i: number, out = new THREE.Color()): THREE.Color {
    const [a, d] = [this.colors.array as Float32Array, this.rockDeg];
    const pavement = this.pavement[i] as number;
    const s = Math.min(1, Math.max(0, ((this.slope[i] as number) - d[0]) / Math.max(1e-9, d[1] - d[0])));
    const rock = s * s * (3 - 2 * s);
    out.setRGB(a[i * 3] as number, a[i * 3 + 1] as number, a[i * 3 + 2] as number);
    return out.multiplyScalar(pavement).add(this.meanBasalt.clone().lerp(this.meanSand, 1 - rock).multiplyScalar(1 - pavement));
  }

  /** The pavement's color at each point, by its pavement's share there (the reef's tint where it is reef) and where it is (M118: Maui Nui Reef's, its strata's). */
  tint(colorOf: (pavement: number, out: THREE.Color, x: number, z: number, i: number) => THREE.Color): void {
    const c = new THREE.Color();
    const a = this.colors.array as Float32Array;
    const at = (this.mesh.geometry.getAttribute('position') as THREE.BufferAttribute).array as Float32Array;
    for (let i = 0; i < this.pavement.length; i++) colorOf(this.pavement[i] as number, c, at[i * 3] as number, at[i * 3 + 2] as number, i).toArray(a, i * 3);
    this.colors.needsUpdate = true;
  }

  /** The pavement's own coral at each point: the share of its pavement and the share of that bleached, each 0 to 1. */
  setCoral(coralOf: (i: number) => readonly [number, number]): void {
    if (!this.coral) return;
    const a = this.coral.array as Float32Array;
    for (let i = 0; i < this.pavement.length; i++) a.set(coralOf(i), i * 2);
    this.coral.needsUpdate = true;
  }

  /**
   * Maui Nui Reef's hard bottom at each point (M177, `ReefPaint`): its turf's, coralline's and rubble's shares of it and
   * its basalt's, each 0 to 1, and its recess, 0 open to 1 the deepest.
   */
  setReef(subOf: (i: number) => readonly [number, number, number, number], recessOf?: (i: number) => number): void {
    if (!this.sub || !this.recess) return;
    const [a, r] = [this.sub.array as Float32Array, this.recess.array as Float32Array];
    for (let i = 0; i < this.pavement.length; i++) {
      a.set(subOf(i), i * 4);
      if (recessOf) r[i] = recessOf(i);
    }
    this.sub.needsUpdate = true;
    if (recessOf) this.recess.needsUpdate = true;
  }

  /** The pavement's own coral at a point now, its share and its bleached share, for the tests. */
  coralAt(i: number): [number, number] {
    const a = this.coral?.array as Float32Array | undefined;
    return a ? [a[i * 2] as number, a[i * 2 + 1] as number] : [0, 0];
  }

  /** The water's light on a point now, the color its paint is multiplied by (`setLight`). */
  waterAt(i: number): [number, number, number] {
    const a = this.water.array as Float32Array;
    return [a[i * 3] as number, a[i * 3 + 1] as number, a[i * 3 + 2] as number];
  }

  /** Its points, each its x and z (the grid's, row by row), for a scene's own weights at each. */
  points(): { x: number; z: number }[] {
    const at = (this.mesh.geometry.getAttribute('position') as THREE.BufferAttribute).array as Float32Array;
    return Array.from({ length: this.pavement.length }, (_, i) => ({ x: at[i * 3] as number, z: at[i * 3 + 2] as number }));
  }

  /** Its color at a point now (the first vertex's), for the tests. */
  colorAt(i = 0): string {
    return new THREE.Color().fromArray(this.colors.array as Float32Array, i * 3).getHexString();
  }

  /** Each point lit by the light at its depth at an hour: the share there of the sun's (or the moon's), seen on the eye's scale, its reds fading first. */
  setLight(sun: HourSun): void {
    this.lastSun = sun;
    const moon = sun.moon?.level ?? 0;
    const strength = Math.max(Math.min(1, sun.level), 1e-3 * moon);
    // The points' light is their fixed depths' at the hour, nothing else: a scene lit again for its camera's depth (the
    // water's light) leaves them as they are (review M122: 31,329 points relit, 1.5 ms, at every half meter of a zoom).
    if (sun.kdPar === this.litKd && strength === this.litStrength) return;
    this.litStrength = strength;
    // The light's share at each depth for the day's clarity, found once a clarity (its logarithm, so an hour's change is a sum).
    const newClarity = sun.kdPar !== this.litKd;
    this.litKd = sun.kdPar;
    const lift = Math.log10(Math.max(strength, 1e-9));
    const gain = 10 ** lift;
    // The painted rock (`paintMesh`) lit again only when the clarity changes or the light has moved a hundredth since it
    // was (M177's review: its 455,754 points made a relight 0.70 ms to 3.98 ms, every frame while the clock runs;
    // `EXTRAS_RELIGHT`).
    const extras = newClarity || !(Math.abs(strength / this.extrasStrength - 1) < EXTRAS_RELIGHT);
    if (extras) this.extrasStrength = strength;
    for (const e of [{ depth: this.depth, logShare: this.logShare, share: this.share, shade: this.shade, fade: this.fade, water: this.water, caustic: this.caustic }, ...(extras ? this.extras : [])]) {
      if (newClarity) {
        for (let i = 0; i < e.depth.length; i++) {
          e.logShare[i] = Math.log10(Math.max(1e-30, lightShare(sun.kdPar, e.depth[i] as number)));
          e.share[i] = 10 ** (e.logShare[i] as number);
        }
      }
      const water = e.water.array as Float32Array;
      const caustic = e.caustic.array as Float32Array;
      for (let i = 0; i < e.depth.length; i++) {
        const log = (e.logShare[i] as number) + lift;
        const level = VISIBLE + (1 - VISIBLE) * Math.min(1, Math.max(0, (log + 4) / 4));
        // Its shade's cool and its water's fade together, found once a shade (`setShade`).
        water[i * 3] = level * (e.fade[i * 3] as number);
        water[i * 3 + 1] = level * (e.fade[i * 3 + 1] as number);
        water[i * 3 + 2] = level * (e.fade[i * 3 + 2] as number);
        // Its caustics as shaded as its light (M174: the user's "softer in shadow"), none brighter in a recess.
        caustic[i] = (e.share[i] as number) * gain * (e.shade[i] as number);
      }
      e.water.needsUpdate = true;
      e.caustic.needsUpdate = true;
    }
  }

  /**
   * The eye balanced to the light at its depth, m (M169, Maui Nui Reef's: the v4 brief's "relatively uniform
   * blue-green cast" that left "the yellows and pinks ... disconnected from the seafloor", the sprites lit against the
   * light at their own depth and the floor not): each point's water's color divided by the water's fade to the eye
   * (`FADE_PER_M`), so a floor deeper than the eye is bluer and one shallower warmer, as a diver's eye sees them.
   */
  adapt(depthM: number): void {
    const z = Math.min(Math.max(0, depthM), FADE_TO_M);
    this.skin.uExAdapt?.value.setRGB(Math.exp(-(FADE_PER_M[0] as number) * z), Math.exp(-(FADE_PER_M[1] as number) * z), Math.exp(-(FADE_PER_M[2] as number) * z));
  }

  /** The eye's balance now (`adapt`), what each point's water's color is divided by as it is drawn. */
  eye(): THREE.Color {
    return this.skin.uExAdapt?.value.clone() ?? new THREE.Color(1, 1, 1);
  }

  /** Each point's own shade, 0 to 1, laid on its light from the next `setLight` (M169). */
  setShade(shadeOf: (i: number) => number): void {
    for (let i = 0; i < this.shade.length; i++) this.shade[i] = shadeOf(i);
    this.shadeFade();
    this.litStrength = Number.NaN;
  }

  /** Each point's fade with depth times its shade, each color to its own power of it (`shadeCool`, M174). */
  private shadeFade(): void {
    for (let i = 0; i < this.shade.length; i++) {
      const shade = this.shade[i] as number;
      for (let k = 0; k < 3; k++) this.fade[i * 3 + k] = (this.depthFade[i * 3 + k] as number) * shade ** (this.shadeCool[k] as number);
    }
  }

  /**
   * Another mesh, its positions in the scene's own space, painted as this floor's hard bottom (M177, Maui Nui Reef's
   * outcrops and loose rocks, the v5 brief's "Make the entire environment feel cohesive"): in its material, its every
   * vertex hard bottom of `sub`'s turf, coralline, rubble and basalt (`setReef`), lit by the light at its depth as the
   * floor's points are, its recess what `recessOf` gives (its undersides and its foot), its shade that much darker
   * (`shadeOf`).
   */
  paintMesh(mesh: THREE.Mesh, sub: readonly [number, number, number, number], recessOf: (x: number, y: number, z: number, upness: number) => number, shadeOf: (recess: number) => number): void {
    const g = mesh.geometry;
    if (!g.getAttribute('normal')) g.computeVertexNormals();
    const [position, normal] = [g.getAttribute('position') as THREE.BufferAttribute, g.getAttribute('normal') as THREE.BufferAttribute];
    const n = position.count;
    const [slope, depth, recess, shade, fade] = [new Float32Array(n), new Float32Array(n), new Float32Array(n), new Float32Array(n), new Float32Array(n * 3)];
    for (let i = 0; i < n; i++) {
      const up = normal.getY(i);
      slope[i] = (Math.acos(Math.min(1, Math.abs(up))) * 180) / Math.PI;
      depth[i] = -position.getY(i);
      recess[i] = recessOf(position.getX(i), position.getY(i), position.getZ(i), up);
      shade[i] = shadeOf(recess[i] as number);
      for (let k = 0; k < 3; k++) fade[i * 3 + k] = Math.exp(-(FADE_PER_M[k] as number) * Math.min(depth[i] as number, FADE_TO_M)) * (shade[i] as number) ** (this.shadeCool[k] as number);
    }
    const water = new THREE.BufferAttribute(new Float32Array(n * 3).fill(1), 3);
    const caustic = new THREE.BufferAttribute(new Float32Array(n), 1);
    g.setAttribute('color', new THREE.BufferAttribute(new Float32Array(n * 3).fill(1), 3));
    g.setAttribute('aSkinLand', new THREE.BufferAttribute(new Float32Array(n), 1));
    g.setAttribute('aSkinReef', new THREE.BufferAttribute(new Float32Array(n).fill(1), 1));
    g.setAttribute('aSlope', new THREE.BufferAttribute(slope, 1));
    g.setAttribute('aDepth', new THREE.BufferAttribute(depth, 1));
    g.setAttribute('aWater', water);
    g.setAttribute('aCaustic', caustic);
    if (this.coral) g.setAttribute('aCoral', new THREE.BufferAttribute(new Float32Array(n * 2), 2));
    g.setAttribute('aSub', new THREE.BufferAttribute(new Float32Array(n * 4).map((_, k) => sub[k % 4] as number), 4));
    g.setAttribute('aRecess', new THREE.BufferAttribute(recess, 1));
    mesh.material = this.mesh.material;
    this.extras.push({ depth, logShare: new Float32Array(n), share: new Float32Array(n), shade, fade, water, caustic });
    // Lit by the next light, or at once by `relight` (M177's review: the kit arriving after the scene was lit, its rock
    // had stayed at full light until the hour or the camera's depth next changed).
    this.litStrength = Number.NaN;
    this.litKd = Number.NaN;
    this.extrasStrength = Number.NaN;
  }

  /**
   * Lit again by the light last given, once its meshes are all painted (`paintMesh`; M180's review: lit at each mesh, the
   * kit's five relit the floor and the rock painted before it each time, 0.13 to 0.16 s, MEASURED under tsx).
   */
  relight(): void {
    if (this.lastSun) this.setLight(this.lastSun);
  }

  /** The caustics slid on by `seconds` of the player's time, as the whole tank's are (`ExhibitView.drift`). */
  drift(seconds: number): void {
    if (!this.causticDrift) return;
    const c = this.causticDrift.value;
    const step = seconds * 0.25;
    const [[ax, ay], [bx, by]] = CAUSTIC_DIRECTIONS;
    const tile = this.causticTexelM * SKIN_TILE_PX;
    const wrap = (v: number): number => ((v % tile) + tile) % tile;
    c.set(wrap(c.x + ax * step), wrap(c.y + ay * step), wrap(c.z + bx * step), wrap(c.w + by * step));
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}


/**
 * A skirt round a floor drawn on a grid (`FloorGrid`): level with the floor's edge, from it out to `outM` from the middle
 * on every side, a quad a grid step along the edge, in the paint's mean sand, lit and fogged as the rest (its owner
 * coloring each edge point's pair by the water's light at that point, as the floor's own paint is, `skirtLight`), so the
 * floor's edge is never seen (M125, the user's sixth brief: "The player should never see the edge of the constructed
 * environment simply by rotating, panning or zooming"; MEASURED, without it, from the Reef camera's box its floor's edge
 * through 75 m of water, a tenth of it shown).
 */
export function floorSkirt(shape: FloorShape, grid: FloorGrid, outM: number): THREE.Mesh {
  const edge = gridEdge(grid);
  const positions = new Float32Array(edge.length * 6);
  edge.forEach(([x, z], i) => {
    const y = shape.heightAt(x, z);
    const out = outM / Math.max(Math.abs(x), Math.abs(z));
    positions.set([x, y, z, x * out, y, z * out], i * 6);
  });
  const index: number[] = [];
  // Wound to face up (the edge runs round anticlockwise seen from below), so it is lit and drawn from above.
  for (let i = 0; i < edge.length; i++) {
    const j = (i + 1) % edge.length;
    index.push(i * 2, j * 2, i * 2 + 1, j * 2, j * 2 + 1, i * 2 + 1);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(index);
  geometry.computeVertexNormals();
  geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(edge.length * 6).fill(1), 3));
  return new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, metalness: 0 }));
}

/** A grid's edge points round it, each side's from one corner to the next (the corner it ends on the next side's first), with each one's index in the grid. */
function gridEdge(grid: FloorGrid): [number, number, number][] {
  const { xs, zs } = grid;
  const [n, m] = [xs.length, zs.length];
  const edge: [number, number, number][] = [];
  for (let c = 0; c < n - 1; c++) edge.push([xs[c] as number, zs[0] as number, c]);
  for (let r = 0; r < m - 1; r++) edge.push([xs[n - 1] as number, zs[r] as number, r * n + n - 1]);
  for (let c = n - 1; c > 0; c--) edge.push([xs[c] as number, zs[m - 1] as number, (m - 1) * n + c]);
  for (let r = m - 1; r > 0; r--) edge.push([xs[0] as number, zs[r] as number, r * n]);
  return edge;
}

/**
 * A skirt drawn as its floor's edge is beside it (M125, the reviews': unlit, its red at midnight 0.148 against the
 * floor's 0.034 at the seam, their estimate by the diffuse light alone; in the mean sand, 2.4 times as bright as the
 * pavement and rock along most of the edge; level, its light up to 33° off the edge's): each of its edge points' pair in
 * the paint of the floor's point there (`PaintedFloor.paintAt`) in that point's water's light (`setLight`), the inner one
 * facing as the floor's edge faces, so the seam matches all round, and seen on the eye's balance as the floor is
 * (`PaintedFloor.adapt`, M169). Laid again on each call, as the light, the eye or the tint changes.
 */
export function skirtLight(skirt: THREE.Mesh, floor: PaintedFloor, grid: FloorGrid): () => void {
  const of = gridEdge(grid).map((e) => e[2]);
  const color = skirt.geometry.getAttribute('color') as THREE.BufferAttribute;
  const [normal, floorNormal] = [skirt.geometry.getAttribute('normal') as THREE.BufferAttribute, floor.mesh.geometry.getAttribute('normal') as THREE.BufferAttribute];
  of.forEach((i, k) => normal.setXYZ(k * 2, floorNormal.getX(i), floorNormal.getY(i), floorNormal.getZ(i)));
  const paint = new THREE.Color();
  const light = new THREE.Color();
  return () => {
    const eye = floor.eye();
    of.forEach((i, k) => {
      const w = floor.waterAt(i);
      floor.paintAt(i, paint).multiply(light.setRGB(w[0] / eye.r, w[1] / eye.g, w[2] / eye.b)).toArray(color.array, k * 2 * 3);
      paint.toArray(color.array, (k * 2 + 1) * 3);
    });
    color.needsUpdate = true;
  };
}

/** Basalt boulders on a floor, one mesh, flat-shaded in the palette's blue-grays, sunk a little into it, each by the place's seed, `spreadM` square about `about`. */
export function boulders(shape: FloorShape, seed: number, count: number, spreadM: number, sizeM: readonly [number, number], about: readonly [number, number] = [0, 0]): THREE.Mesh {
  const parts: THREE.BufferGeometry[] = [];
  const color = new THREE.Color();
  for (let k = 0; k < count; k++) {
    const [x, z] = [about[0] + (hash3(seed, k, 131) - 0.5) * spreadM, about[1] + (hash3(seed, k, 132) - 0.5) * spreadM];
    const size = sizeM[0] + (sizeM[1] - sizeM[0]) * hash3(seed, k, 133) ** 2;
    const g = new THREE.IcosahedronGeometry(size / 2, 0);
    const p = g.getAttribute('position') as THREE.BufferAttribute;
    // Each corner pushed in or out by its own draw (the same corner of two faces alike), flattened, sunk a little.
    for (let i = 0; i < p.count; i++) {
      const v = new THREE.Vector3().fromBufferAttribute(p, i);
      const key = Math.round(v.x * 1000) * 7 + Math.round(v.y * 1000) * 13 + Math.round(v.z * 1000);
      v.multiplyScalar(0.75 + 0.5 * hash3(seed + k, key, 134));
      p.setXYZ(i, v.x + x, v.y * 0.6 + shape.heightAt(x, z) + size * 0.12, v.z + z);
    }
    const colors = new Float32Array(p.count * 3);
    color.setHex(PAINT_HEX.basalt[Math.floor(hash3(seed, k, 135) * PAINT_HEX.basalt.length)] as number);
    for (let i = 0; i < p.count; i++) color.toArray(colors, i * 3);
    g.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    g.computeVertexNormals();
    parts.push(g);
  }
  const merged = mergeGeometries(parts);
  for (const g of parts) g.dispose();
  return new THREE.Mesh(merged, new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 1, metalness: 0 }));
}

/** Geometries of the same attributes, unindexed, as one. */
function mergeGeometries(parts: readonly THREE.BufferGeometry[]): THREE.BufferGeometry {
  const names = Object.keys(parts[0]?.attributes ?? {});
  const out = new THREE.BufferGeometry();
  for (const name of names) {
    const size = (parts[0]?.getAttribute(name) as THREE.BufferAttribute).itemSize;
    const total = parts.reduce((n, g) => n + g.getAttribute(name).count, 0);
    const array = new Float32Array(total * size);
    let at = 0;
    for (const g of parts) {
      const a = g.getAttribute(name) as THREE.BufferAttribute;
      array.set(a.array as Float32Array, at);
      at += a.count * size;
    }
    out.setAttribute(name, new THREE.BufferAttribute(array, size));
  }
  return out;
}

/**
 * A plain material given the water's fog alone (M110): the slab method's fog laid on its outgoing light, as the floor's
 * skin lays it (`skin.ts`), so outcrops, colonies and boulders fade into the water as the floor behind them does.
 */
export function fogOnly(material: THREE.MeshStandardMaterial | THREE.MeshBasicMaterial): FogUniforms {
  const fog = noFog();
  const before = material.onBeforeCompile;
  material.onBeforeCompile = (shader, renderer) => {
    before.call(material, shader, renderer);
    Object.assign(shader.uniforms, fog);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vFogWorld;')
      .replace('#include <worldpos_vertex>', `#include <worldpos_vertex>
      #ifdef USE_INSTANCING
        vFogWorld = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xyz;
      #else
        vFogWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;
      #endif`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${WATER_FOG}varying vec3 vFogWorld;`)
      .replace('#include <opaque_fragment>', `vec4 habitatFog = waterFog(vFogWorld);
      outgoingLight = mix(outgoingLight, habitatFog.rgb, habitatFog.a);
      #include <opaque_fragment>`);
  };
  const key = material.customProgramCacheKey.bind(material);
  material.customProgramCacheKey = () => `${key()}-habitat-fog`;
  material.needsUpdate = true;
  return fog;
}
